// Meldet Abstürze aus dem Browser an POST /api/client-error.
// Bewusst ohne die Axios-Instanz `api`: deren 401-Interceptor leitet auf /login um,
// und ein abgelehntes Promise aus dem Melder würde erneut `unhandledrejection`
// auslösen — Endlosschleife.
import { APP_VERSION } from '../version';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_PRO_SEITE = 3;
const WIEDERHOLUNG_MS = 60000;

// Der Zustand liegt auf `window`: das Modul kann über zwei Pfade (Alias und relativ)
// geladen werden, und HMR ersetzt es. Ohne gemeinsamen Zustand würde dieselbe
// Meldung mehrfach gesendet.
const zustand = () => {
  if (!window.__timenyzerFehlerMelder) {
    window.__timenyzerFehlerMelder = { gemeldet: 0, zuletzt: new Map(), installiert: false };
  }
  return window.__timenyzerFehlerMelder;
};

// Nur der Pfad, niemals Query oder Fragment: im Query steht der Passwort-Token.
// Das Token im Pfad von /teacher-confirm/<token> wird ersetzt.
export const seitenPfad = () => {
  const p = window.location.pathname || '';
  return p.replace(/^\/teacher-confirm\/.+$/, '/teacher-confirm/…');
};

const kuerzen = (s) => (typeof s === 'string' ? s.slice(0, 4096) : '');

// React 19 verpackt Fehler aus onRecoverableError in eine Hülle (Minified React error #520);
// die echte Ursache steht in `cause`. Gelesen werden ausschliesslich name/message/stack —
// niemals config/request/response, dort stehen Tokens und der Lexware-Schlüssel.
// "<name>: <message>" wird nur vorangestellt, wenn der stack nicht ohnehin damit beginnt:
// sonst stünde die Zeile doppelt und würde von den 4096 Zeichen fressen.
const ausCause = (cause) => {
  if (cause === null || cause === undefined) return '';
  if (typeof cause !== 'object') return kuerzen(String(cause));
  const name = typeof cause.name === 'string' ? cause.name : '';
  const message = typeof cause.message === 'string' ? cause.message : '';
  const stack = typeof cause.stack === 'string' ? cause.stack : '';
  const kopf = [name, message].filter(Boolean).join(': ');
  if (!kopf) return kuerzen(stack);
  if (!stack) return kuerzen(kopf);
  return kuerzen(stack.startsWith(kopf) ? stack : `${kopf}\n${stack}`);
};

export const meldeFehler = ({ message, stack, componentStack, cause }) => {
  const z = zustand();
  const seite = seitenPfad();
  const text = String(message || 'Unbekannter Fehler').slice(0, 2000);
  const schluessel = `${text}|${(stack || '').split('\n')[0]}|${seite}`;
  const jetzt = Date.now();
  if (z.gemeldet >= MAX_PRO_SEITE) return;
  const vorher = z.zuletzt.get(schluessel);
  if (vorher && jetzt - vorher < WIEDERHOLUNG_MS) return;
  z.zuletzt.set(schluessel, jetzt);
  z.gemeldet += 1;
  try {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND_URL}/api/client-error`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message: text,
        stack: kuerzen(stack),
        component_stack: kuerzen(componentStack),
        page: seite,
        user_agent: (navigator.userAgent || '').slice(0, 400),
        cause: ausCause(cause),
        app_version: String(APP_VERSION || '').slice(0, 40),
        viewport_w: Math.round(window.innerWidth || 0),
        viewport_h: Math.round(window.innerHeight || 0),
        html_lang: String(document.documentElement.lang || '').slice(0, 20),
        html_class: String(document.documentElement.className || '').slice(0, 200),
      }),
    }).catch(() => {});
  } catch {
    /* Melden darf nie selbst einen Fehler auslösen */
  }
};

// Aus einer abgelehnten Promise ausschliesslich name/message/stack lesen.
// `config`, `request` und `response` werden nie berührt: dort stehen Zugangsdaten,
// Tokens und der Lexware-Schlüssel.
const ausReason = (reason) => {
  if (!reason || typeof reason !== 'object') {
    return { message: String(reason), stack: '' };
  }
  const name = typeof reason.name === 'string' ? reason.name : '';
  const message = typeof reason.message === 'string' ? reason.message : '';
  const stack = typeof reason.stack === 'string' ? reason.stack : '';
  return { message: [name, message].filter(Boolean).join(': ') || 'Abgelehnte Promise', stack };
};

// Axios-Fehler mit HTTP-Status sind normale Server-Antworten, keine Abstürze.
const hatHttpStatus = (reason) =>
  !!(reason && typeof reason === 'object' && reason.response && reason.response.status);

export const installiereFehlerMelder = () => {
  const z = zustand();
  if (z.installiert) return;
  z.installiert = true;
  window.addEventListener('error', (ev) => {
    meldeFehler({
      message: ev?.message || ev?.error?.message,
      stack: ev?.error?.stack || '',
      cause: ev?.error?.cause,
    });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev?.reason;
    if (hatHttpStatus(reason)) return;
    meldeFehler(ausReason(reason));
  });
};
